import { Box, ThemeProvider, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { TireFormData } from 'app/shared/model/tire.model';
import { TireDiscardReason } from 'app/shared/model/enumerations/tire-discard-reason.model';
import { BoxForm, FormControlDefault, MenuItemStyled, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { RegisterLayout } from 'app/shared/layout/register/RegisterLayout';
import { RegisterTitle } from 'app/shared/layout/register/RegisterTitle';
import React, { useEffect, useRef, useState } from 'react';
import { translate, ValidatedField } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import { createEntity, getEntity, reset, updateEntity } from './tire.reducer';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import { toast } from 'react-toastify';
import { AUTHORITIES } from 'app/config/constants';
import { hasAnyAuthority } from 'app/shared/auth/private-route';

export const TireUpdate = (props: RouteComponentProps<{ id: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const hasSaved = useRef(false);

  const tireEntity = useAppSelector(state => state.tire.entity);
  const loading = useAppSelector(state => state.tire.loading);
  const updating = useAppSelector(state => state.tire.updating);
  const updateSuccess = useAppSelector(state => state.tire.updateSuccess);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  
  const [formData, setFormData] = useState<TireFormData>({
    dot: null,
    groove: null,
    marking: null,
    odometer: null,
    recap: null,
    status: null,
    tireModel: null,
    tireTreadModel: null,
    totalCost: null,
    discardReason: null,
  });

  useEffect(() => {
    if (!isNew && tireEntity) {
      setFormData({
        dot: tireEntity.dot,
        groove: tireEntity.groove?.toString().replace('.', ','),
        marking: tireEntity.marking,
        odometer: tireEntity.odometer?.toString().replace('.', ','),
        recap: tireEntity.recap,
        status: tireEntity.status,
        tireModel: tireEntity.tireModel,
        tireTreadModel: tireEntity.tireTreadModel,
        totalCost: tireEntity.totalCost?.toString().replace('.', ','),
        discardReason: tireEntity.discardReason,
      });
    }
  }, [tireEntity]);

  const handleClose = () => {
    props.history.push('/tire' + props.location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
    }
  }, []);

  useEffect(() => {
    if (updateSuccess && hasSaved.current) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    const entity = {
      ...tireEntity,
      ...values,
      groove: values.groove ? Number(String(values.groove).replace(',', '.')) : null,
      odometer: values.odometer ? Number(String(values.odometer).replace(',', '.')) : null,
      totalCost: values.totalCost ? Number(String(values.totalCost).replace(',', '.')) : null,
    };

    hasSaved.current = true;

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prevFormData => {
      const newFormData = { ...prevFormData, [fieldName]: value };

    if (fieldName === 'tireModel') {
      newFormData['groove'] = value?.groove ?? null;
    }

    if (fieldName === 'status' && value !== 'WASTE') {
      newFormData['discardReason'] = null;
    }

      return newFormData;
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData?.marking) {
      return toast.error('Insira uma marcação (exemplo: 43815)');
    }

    if (!formData?.tireModel) {
      return toast.error('Selecione um modelo (exemplo: Bridgestone M792 ECO 295/80R22.5)');
    }

    if (!formData?.groove) {
      return toast.error('Insira um valor de sulco (exemplo: 23.5)');
    }

    if (!formData?.totalCost) {
      return toast.error('Insira um valor de custo total (exemplo: 2397.51)');
    }

    if (formData?.status === 'WASTE' && !formData?.discardReason) {
      return toast.error('Selecione um motivo para o descarte do pneu');
    }

    saveEntity(formData);
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <RegisterLayout>
          <Box sx={{ width: { xs: '95%', sm: '70%', lg: '50%' } }} justifyContent={'center'}>
            <RegisterTitle isNew={isNew} contentKey={'fleetSenseApp.tire'} />
            <Box>
              {loading ? (
                <Box>
                  <Typography>Loading...</Typography>
                </Box>
              ) : (
                <Box margin={'0 auto'} width={'100%'}>
                  <BoxForm onSubmit={handleSubmit}>
                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.tire.marking')}</TypographyForms>
                      <TextFieldForms
                        value={formData['marking']}
                        id={'tire-marking'}
                        data-cy={'marking'}
                        name={'marking'}
                        onChange={e => handleInputChange('marking', e.target.value)}
                      />
                    </FormControlDefault>

                    <FormControlDefault>
                      <SelectPaginate
                        label={translate('fleetSenseApp.tire.tireModel')}
                        apiUrl={'api/tire-models'}
                        onChange={value => handleInputChange('tireModel', value)}
                        value={formData['tireModel']}
                        valueName="name"
                        icon="sort-down"
                        statusFilter
                        filterStyle={true}
                        searchName="nameSearch"
                        displayFormat={option => `${option.tireManufacturer?.name ?? ''} ${option.name ?? ''} ${option.specification ?? ''}`}
                      />
                    </FormControlDefault>

                    <FormControlDefault>
                      <SelectPaginate
                        label={translate('fleetSenseApp.tire.tireTreadModel')}
                        apiUrl={'api/tire-tread-models'}
                        onChange={value => handleInputChange('tireTreadModel', value)}
                        value={formData['tireTreadModel']}
                        valueName="name"
                        icon="sort-down"
                        statusFilter
                        filterStyle={true}
                        searchName="nameSearch"
                        displayFormat={option => `${option.tireTreadManufacturer?.name ?? ''} ${option.name ?? ''} ${option.specification ?? ''}`}
                      />
                    </FormControlDefault>

                    {!isNew && (
                      <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.tire.status')}</TypographyForms>
                      <TextFieldForms
                        value={formData['status'] || ''}
                        id={'tire-status'}
                        data-cy={'status'}
                        name={'status'}
                        onChange={e => handleInputChange('status', e.target.value)}
                        select
                      >
                        <MenuItemStyled value="STOCK">{translate('fleetSenseApp.TireStatus.STOCK')}</MenuItemStyled>
                        <MenuItemStyled value="RECAP">{translate('fleetSenseApp.TireStatus.RECAP')}</MenuItemStyled>
                        <MenuItemStyled value="WASTE">{translate('fleetSenseApp.TireStatus.WASTE')}</MenuItemStyled>

                      </TextFieldForms>
                    </FormControlDefault>
                    )}

                    {formData.status === 'WASTE' && (
                      <FormControlDefault variant={'outlined'}>
                        <TypographyForms>{translate('fleetSenseApp.tire.discardReason')}</TypographyForms>
                        <TextFieldForms
                          value={formData['discardReason'] || ''}
                          id={'tire-discardReason'}
                          data-cy={'discardReason'}
                          name={'discardReason'}
                          onChange={e => handleInputChange('discardReason', e.target.value)}
                          select
                        >
                          {Object.keys(TireDiscardReason).map(item => (
                            <MenuItemStyled key={item} value={item}>
                              {translate(`fleetSenseApp.TireDiscardReason.${item}`)}
                            </MenuItemStyled>
                          ))}
                        </TextFieldForms>
                      </FormControlDefault>
                    )}

                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.tire.groove')}</TypographyForms>
                      <TextFieldForms
                        value={formData['groove']}
                        id={'tire-groove'}
                        data-cy={'groove'}
                        name={'groove'}
                        onChange={e => {
                          const value = e.target.value;
                          if (/^\d*(,\d*)?$/.test(value)) {
                            handleInputChange('groove', value);
                          }
                        }}
                        inputProps={{
                          inputMode: 'decimal',
                          pattern: '\\d*([,.]\\d*)?',
                        }}
                        disabled={tireEntity?.status === 'INSTALLED'}                        
                      />
                    </FormControlDefault>

                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.tire.odometer')}</TypographyForms>
                      <TextFieldForms
                        value={formData['odometer']}
                        id={'tire-odometer'}
                        data-cy={'odometer'}
                        name={'odometer'}
                        onChange={e => {
                          const value = e.target.value;
                          if (/^\d*(,\d*)?$/.test(value)) {
                            handleInputChange('odometer', value);
                          }
                        }}
                        inputProps={{
                          inputMode: 'decimal',
                          pattern: '\\d*([,.]\\d*)?',
                        }}
                        disabled={tireEntity?.status === 'INSTALLED'}
                      />
                    </FormControlDefault>

                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.tire.recap')}</TypographyForms>
                      <TextFieldForms
                        value={formData['recap']}
                        id={'tire-recap'}
                        data-cy={'recap'}
                        name={'recap'}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          handleInputChange('recap', value);
                        }}
                        onKeyDown={e => {
                          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}                        
                      />
                    </FormControlDefault>

                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.tire.totalCost')}</TypographyForms>
                      <TextFieldForms
                        value={formData['totalCost']}
                        id={'tire-totalCost'}
                        data-cy={'totalCost'}
                        name={'totalCost'}
                        onChange={e => {
                          const value = e.target.value;
                          if (/^\d*(,\d*)?$/.test(value)) {
                            handleInputChange('totalCost', value);
                          }
                        }}
                      />
                    </FormControlDefault>

                    <FormControlDefault variant={'outlined'}>
                      <TypographyForms>{translate('fleetSenseApp.tire.dot')}</TypographyForms>
                      <TextFieldForms
                        value={formData['dot']}
                        id={'tire-dot'}
                        data-cy={'dot'}
                        name={'dot'}
                        onChange={e => handleInputChange('dot', e.target.value)}
                      />
                    </FormControlDefault>

                    <RegisterButton />
                  </BoxForm>
                </Box>
              )}
            </Box>
          </Box>
        </RegisterLayout>
      </ThemeProvider>
    </>
  );
};

export default TireUpdate;
